import { useQuery } from "@tanstack/react-query";
import { Locales } from "@/lib/i18n";
import {
  fetchMultiplePrismicProduct,
  fetchMultipleProducts,
  fetchPrismicProduct,
  fetchSingleProduct,
  fetchStorefrontProductAvailability,
} from "@/queries/product";
import { StoreProduct } from "@medusajs/types";
import { getQueryClient } from "@/lib/query";

/**
 * Type definition for a single product query key.
 * Example: ["product", "en-US", "product-uid"]
 */
export type ProductQueryKey = ["product", Locales, string];

export type ProductsQueryKey = ["products", Locales, string[]];

/**
 * Generates a query key for a single product.
 * This helps ensure consistent caching of product queries in React Query.
 */
export const productKey = (locale: Locales, uid: string): ProductQueryKey => ["product", locale, uid];

/**
 * Generates a query key for a batch of products.
 * Sorting the uids array ensures that key is stable to prevent cache misses.
 */
export const batchKey = (locale: Locales, uids: string[]): ProductsQueryKey => [
  "products",
  locale,
  uids.sort(), // ensures stable key order for caching
];

/**
 * React Query hook to fetch a single product.
 *
 * @param locale - Current locale
 * @param uid - Product unique identifier (uid)
 * @param enabled - Optional flag to enable/disable query (default: true)
 *
 * @returns Query result (loading state, data, error, etc.)
 */
export const useProduct = (locale: Locales, uid: string, enabled = true) => {
  const queryClient = getQueryClient();

  return useQuery({
    queryKey: productKey(locale, uid),
    queryFn: () => fetchSingleProduct(locale, uid),
    enabled,
    refetchOnMount: "always",
    initialData: () => {
      const batchQueries = queryClient.getQueriesData({
        predicate: ({ queryKey }) => Array.isArray(queryKey) && queryKey[0] === "products" && queryKey[1] === locale,
      });

      for (const [, data] of batchQueries) {
        if (!data) continue;

        const productData = (data as Record<string, StoreProduct>)[uid];
        if (productData) return productData;
      }

      return queryClient.getQueryData(productKey(locale, uid));
    },
  });
};

/**
 * React Query hook to fetch multiple products (batch query).
 *
 * @param locale - Current locale
 * @param uids - Array of product unique identifiers
 * @param enabled - Optional flag to enable/disable query (default: true)
 *
 * @returns Query result (loading state, data, error, etc.)
 */
export const useProducts = (locale: Locales, uids: string[], enabled = true) => {
  return useQuery({
    queryKey: batchKey(locale, uids),
    queryFn: () => fetchMultipleProducts(locale, uids),
    enabled,
  });
};

export const useStorefrontProductAvailability = (locale: Locales, slug: string, enabled = true) => {
  return useQuery({
    queryKey: ["storefront-product-availability", locale, slug],
    queryFn: () => fetchStorefrontProductAvailability(locale, slug),
    enabled: enabled && Boolean(slug),
    staleTime: 5 * 60 * 1000,
  });
};

export const usePrismicProduct = (locale: Locales, uid: string, enabled = true) => {
  return useQuery({
    queryKey: ["prismic-product", locale, uid],
    queryFn: () => fetchPrismicProduct(locale, uid),
    enabled,
  });
};

export const usePrismicProducts = (locale: Locales, uids: string[], params: object = {}, enabled = true) => {
  return useQuery({
    queryKey: ["prismic-products", locale, uids.sort()],
    queryFn: () => fetchMultiplePrismicProduct(locale, uids, params),
    enabled,
  });
};
